@if (isLoading) {
  <section class="website-builder__loading py-5 d-flex justify-content-center">
    <ion-spinner name="crescent"></ion-spinner>
  </section>
} @else if (loadError) {
  <app-api-load-error
    title="Website builder unavailable"
    message="We couldn't load the website builder right now. Please try again."
    retryLabel="Retry"
    (retry)="loadBuilderData(true)"
  ></app-api-load-error>
} @else if (activeDocument && activeWebsite && activePage) {
  <div class="website-builder container-fluid px-0 pt-0 pb-0">
    <nav class="website-builder__breadcrumb mb-1" aria-label="Website builder breadcrumb">
      <a class="website-builder__breadcrumb-link" routerLink="/websites">Websites</a>
      <span class="website-builder__breadcrumb-separator">/</span>
      <span class="website-builder__breadcrumb-link">{{ activeWebsite.name }}</span>
      <span class="website-builder__breadcrumb-separator">/</span>
      <span class="website-builder__breadcrumb-current">{{ activePage.name }}</span>
    </nav>

    <section class="website-builder__surface website-builder__surface--top p-2" (contextmenu)="openHeaderContextMenu($event)">
      <div class="website-builder__toolbar">
        <div class="website-builder__toolbar-group">
          <div class="website-builder__toolbar-actions">
            <button type="button" class="website-builder__tool-button" [class.is-active]="showPagesDrawer" title="Pages" (click)="togglePagesDrawer()">
              <ion-icon name="document-text-outline"></ion-icon>
              <span>Pages</span>
            </button>
            <button type="button" class="website-builder__tool-button" [class.is-active]="showLayersDrawer" title="Layers" (click)="toggleLayersDrawer()">
              <ion-icon name="layers-outline"></ion-icon>
              <span>Layers</span>
            </button>
          </div>
        </div>

        <div class="website-builder__toolbar-group">
          <div class="website-builder__toolbar-actions">
            <button type="button" class="website-builder__tool-button is-primary" title="Add section" (click)="openSectionModal()">
              <ion-icon name="sparkles-outline"></ion-icon>
              <span>Add section</span>
            </button>
            <button type="button" class="website-builder__tool-button" title="Add component" (click)="openComponentModal()">
              <ion-icon name="cube-outline"></ion-icon>
              <span>Add component</span>
            </button>
          </div>
        </div>

        <div class="website-builder__toolbar-group">
          <div class="website-builder__toolbar-actions">
            <button type="button" class="website-builder__tool-button website-builder__tool-button--icon" title="Undo" [disabled]="!builderService.canUndo()" (click)="builderService.undo()">
              <ion-icon name="arrow-undo-outline"></ion-icon>
            </button>
            <button type="button" class="website-builder__tool-button website-builder__tool-button--icon" title="Redo" [disabled]="!builderService.canRedo()" (click)="builderService.redo()">
              <ion-icon name="arrow-redo-outline"></ion-icon>
            </button>
            <button type="button" class="website-builder__tool-button" title="Save a whole-website version" [disabled]="isSavingWebsiteVersion" (click)="saveVersion()">
              <ion-icon name="save-outline"></ion-icon>
              <span>{{ isSavingWebsiteVersion ? 'Saving version...' : 'Save version' }}</span>
            </button>
          </div>
        </div>

        <div class="website-builder__toolbar-group website-builder__toolbar-group--end">
          <div class="website-builder__theme-tools">
            <div class="website-builder__theme-select-wrap">
              <select
                class="website-builder__select website-builder__select--compact"
                [ngModel]="currentCanvasThemePackageId"
                (ngModelChange)="selectCanvasThemePackage($event)"
              >
                @for (themePackage of canvasThemeService.packages(); track themePackage.id) {
                  <option [value]="themePackage.id">{{ themePackage.name }}</option>
                }
              </select>
            </div>

            <label class="website-builder__theme-toggle" title="Toggle canvas theme">
              <ion-icon name="sunny-outline"></ion-icon>
              <input
                type="checkbox"
                [ngModel]="currentCanvasThemeMode === 'dark'"
                (ngModelChange)="setCanvasThemeMode($event ? 'dark' : 'light')"
              />
              <span class="website-builder__theme-toggle-track">
                <span class="website-builder__theme-toggle-thumb"></span>
              </span>
              <ion-icon name="moon-outline"></ion-icon>
            </label>

            <button type="button" class="website-builder__tool-button" title="Canvas themes" (click)="openThemeManagerModal()">
              <ion-icon name="color-palette-outline"></ion-icon>
              <span>Themes</span>
            </button>
          </div>
          <span class="website-builder__autosave-state" [class.is-saving]="isSavingBuilderState">
            {{
              isSavingBuilderState
                ? 'Saving draft...'
                : (hasPendingBuilderChanges ? 'Local draft pending' : 'All changes saved')
            }}
          </span>
        </div>
      </div>
    </section>

    <section class="website-builder__workspace" (contextmenu)="openWorkspaceContextMenu($event)">
      <div class="website-builder__workspace-body" [class.has-sidebar]="showLeftSidebar">
        @if (showPagesDrawer || showLayersDrawer) {
          <aside class="website-builder__workspace-sidebar">
            @if (showPagesDrawer) {
              <section class="website-builder__side-panel website-builder__side-panel--pages">
                <div class="website-builder__side-panel-header">
                  <div>
                    <h3 class="website-builder__section-title mb-0 bw-title-with-info">
                      Website pages
                      <app-info-tooltip compact placement="right" text="Switch to another page without leaving the builder. Internal links use this same page list."></app-info-tooltip>
                    </h3>
                  </div>

                  <button type="button" class="website-builder__icon-button" title="Close pages" (click)="closePagesDrawer()">
                    <ion-icon name="close-outline"></ion-icon>
                  </button>
                </div>

                <div class="website-builder__side-panel-body">
                  <section class="website-builder__side-panel-section">
                    <button type="button" class="website-builder__side-panel-section-toggle" [class.is-open]="pagesPanelSections.current" (click)="togglePagesPanelSection('current')">
                      <span>Current page</span>
                      <ion-icon name="chevron-forward-outline"></ion-icon>
                    </button>

                    @if (pagesPanelSections.current) {
                      <div class="website-builder__side-panel-section-body">
                        <div class="website-builder__page-current-card">
                          <div class="website-builder__page-current-head">
                            <span class="website-builder__page-name">{{ activePage.name }}</span>
                            <div class="website-builder__page-flags">
                              @if (activePage.isHome) {
                                <span class="website-builder__page-chip">Home</span>
                              }
                              <span class="website-builder__page-chip is-active">Current</span>
                            </div>
                          </div>
                          <span class="website-builder__page-path">{{ activePage.path }}</span>
                        </div>
                      </div>
                    }
                  </section>

                  <section class="website-builder__side-panel-section">
                    <button type="button" class="website-builder__side-panel-section-toggle" [class.is-open]="pagesPanelSections.all" (click)="togglePagesPanelSection('all')">
                      <span>All pages</span>
                      <ion-icon name="chevron-forward-outline"></ion-icon>
                    </button>

                    @if (pagesPanelSections.all) {
                      <div class="website-builder__side-panel-section-body">
                        <div class="website-builder__page-list">
                          @for (page of builderService.pages(); track trackPage($index, page)) {
                            <button
                              type="button"
                              class="website-builder__page-button"
                              [class.is-active]="page.publicId === activePage.publicId"
                              (click)="selectPage(page)"
                              (contextmenu)="openPageContextMenu($event, page)"
                            >
                              <div class="website-builder__page-button-top">
                                <span class="website-builder__page-name">{{ page.name }}</span>
                                <div class="website-builder__page-flags">
                                  @if (page.isHome) {
                                    <span class="website-builder__page-chip">Home</span>
                                  }
                                  @if (page.publicId === activePage.publicId) {
                                    <span class="website-builder__page-chip is-active">Current</span>
                                  }
                                </div>
                              </div>
                              <span class="website-builder__page-path">{{ page.path }}</span>
                            </button>
                          }
                        </div>
                      </div>
                    }
                  </section>
                </div>
              </section>
            }

            @if (showLayersDrawer) {
              <section class="website-builder__side-panel website-builder__side-panel--layers">
                <div class="website-builder__side-panel-header">
                  <div>
                    <h3 class="website-builder__section-title mb-0 bw-title-with-info">
                      Page layers
                      <app-info-tooltip compact placement="right" text="The layer tree mirrors the current page document. Collapse branches you do not need and right-click rows to add nested content."></app-info-tooltip>
                    </h3>
                  </div>

                  <button type="button" class="website-builder__icon-button" title="Close layers" (click)="closeLayersDrawer()">
                    <ion-icon name="close-outline"></ion-icon>
                  </button>
                </div>

                <div class="website-builder__side-panel-body">
                  <section class="website-builder__side-panel-section">
                    <button type="button" class="website-builder__side-panel-section-toggle" [class.is-open]="layersPanelSections.selection" (click)="toggleLayersPanelSection('selection')">
                      <span>Selection</span>
                      <ion-icon name="chevron-forward-outline"></ion-icon>
                    </button>

                    @if (layersPanelSections.selection) {
                      <div class="website-builder__side-panel-section-body">
                        @if (selectedNode; as node) {
                          <div class="website-builder__selection-card">
                            <span class="website-builder__layer-label">{{ node.label }}</span>
                            <span class="website-builder__layer-type">{{ node.type | titlecase }}</span>
                            @if (isContainer(node)) {
                              <span class="website-builder__selection-meta">{{ node.children.length }} child{{ node.children.length === 1 ? '' : 'ren' }}</span>
                            }
                          </div>
                        } @else {
                          <div class="website-builder__selection-card">
                            <span class="website-builder__layer-label">{{ activeDocument.root.label }}</span>
                            <span class="website-builder__layer-type">Page root</span>
                          </div>
                        }
                      </div>
                    }
                  </section>

                  <section class="website-builder__side-panel-section">
                    <button type="button" class="website-builder__side-panel-section-toggle" [class.is-open]="layersPanelSections.tree" (click)="toggleLayersPanelSection('tree')">
                      <span>Structure</span>
                      <ion-icon name="chevron-forward-outline"></ion-icon>
                    </button>

                    @if (layersPanelSections.tree) {
                      <div class="website-builder__side-panel-section-body">
                        <div class="website-builder__layers">
                          <div class="website-builder__tree-node">
                            <div class="website-builder__tree-row is-root" [class.is-selected]="builderService.selectedNodeId() === activeDocument.root.id">
                              @if (activeDocument.root.children.length) {
                                <button type="button" class="website-builder__tree-toggle" [class.is-open]="isLayerExpanded(activeDocument.root.id)" (click)="toggleLayerExpansion(activeDocument.root.id, $event)" aria-label="Toggle root children">
                                  <ion-icon name="chevron-forward-outline"></ion-icon>
                                </button>
                              } @else {
                                <span class="website-builder__tree-toggle-spacer" aria-hidden="true"></span>
                              }

                              <button
                                type="button"
                                class="website-builder__layer-button website-builder__layer-button--tree is-root"
                                (click)="selectLayerNode(activeDocument.root.id, $event)"
                                (contextmenu)="openLayerContextMenu($event, activeDocument.root)"
                              >
                                <ion-icon name="layers-outline"></ion-icon>
                                <div class="website-builder__layer-meta">
                                  <span class="website-builder__layer-label">{{ activeDocument.root.label }}</span>
                                  <span class="website-builder__layer-type">Page root</span>
                                </div>
                              </button>
                            </div>

                            @if (isLayerExpanded(activeDocument.root.id)) {
                              <div class="website-builder__tree-children">
                                @for (child of activeDocument.root.children; track trackNode($index, child)) {
                                  <ng-container [ngTemplateOutlet]="layerNode" [ngTemplateOutletContext]="{ $implicit: child, depth: 1 }"></ng-container>
                                }
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    }
                  </section>
                </div>
              </section>
            }
          </aside>
        }

        <div class="website-builder__workspace-main">
          <div class="website-builder__viewport-toolbar">
            <div class="website-builder__viewport-mainbar">
              <div class="website-builder__viewport-mainbar-spacer"></div>

              <div class="website-builder__viewport-controls">
                <div class="website-builder__toolbar-meta">
                  <span class="website-builder__toolbar-label">Dimensions:</span>
                  <app-info-tooltip compact placement="bottom" text="Resize the editing viewport like a browser device frame. Presets switch to common sizes and manual resizing changes the mode to Responsive."></app-info-tooltip>
                </div>

                <span
                  class="website-builder__viewport-control website-builder__viewport-control--mode"
                  [class.is-active]="previewMode === 'responsive'"
                >
                  Responsive
                </span>

                <label class="website-builder__viewport-input-wrap">
                  <input
                    class="website-builder__viewport-input"
                    type="number"
                    step="1"
                    [ngModel]="canvasWidth"
                    (ngModelChange)="updateViewportWidth($event)"
                  />
                </label>

                <span class="website-builder__viewport-separator">x</span>

                <label class="website-builder__viewport-input-wrap">
                  <input
                    class="website-builder__viewport-input"
                    type="number"
                    step="1"
                    [ngModel]="canvasHeight"
                    (ngModelChange)="updateViewportHeight($event)"
                  />
                </label>

                <div
                  class="website-builder__viewport-combo"
                  [class.is-open]="showZoomPresetMenu"
                  (focusout)="handleViewportZoomFocusOut($event)"
                >
                  <div class="website-builder__viewport-zoom-field">
                    <input
                      class="website-builder__viewport-input website-builder__viewport-input--zoom"
                      type="number"
                      step="1"
                      [ngModel]="canvasZoomPercentage"
                      (ngModelChange)="updateViewportZoom($event)"
                      (focus)="openViewportZoomMenu()"
                      (click)="openViewportZoomMenu()"
                    />
                    <span class="website-builder__viewport-suffix">%</span>
                    <div class="website-builder__viewport-stepper">
                      <button
                        type="button"
                        class="website-builder__viewport-stepper-button website-builder__viewport-stepper-button--up"
                        aria-label="Increase zoom"
                        (click)="nudgeViewportZoom(1)"
                      ></button>
                      <button
                        type="button"
                        class="website-builder__viewport-stepper-button website-builder__viewport-stepper-button--down"
                        aria-label="Decrease zoom"
                        (click)="nudgeViewportZoom(-1)"
                      ></button>
                    </div>
                  </div>

                  <div class="website-builder__viewport-combo-menu" role="listbox" aria-label="Zoom presets">
                    @for (zoomPreset of zoomPresetOptions; track zoomPreset) {
                      <button
                        type="button"
                        class="website-builder__viewport-combo-option"
                        (click)="selectViewportZoomPreset(zoomPreset)"
                      >
                        {{ zoomPreset }}%
                      </button>
                    }
                  </div>
                </div>
              </div>

              <div class="website-builder__viewport-actions">
                <label class="website-builder__viewport-check">
                  <input type="checkbox" [checked]="showViewportGrid" (change)="toggleViewportGrid()" />
                  <span>Grid</span>
                </label>
                <label class="website-builder__viewport-check">
                  <input type="checkbox" [checked]="showViewportRulers" (change)="toggleViewportRulers()" />
                  <span>Rulers</span>
                </label>
                <button type="button" class="website-builder__viewport-control website-builder__viewport-control--preview" (click)="openPreviewModal()">
                  <ion-icon name="eye-outline"></ion-icon>
                  <span>Preview</span>
                </button>
              </div>
            </div>

            <div class="website-builder__viewport-presetsbar" role="toolbar" aria-label="Viewport presets">
              @for (preset of previewPresets; track preset.id) {
                <button
                  type="button"
                  class="website-builder__viewport-preset"
                  [class.is-active]="previewMode === preset.id"
                  [title]="preset.label"
                  (click)="setPreviewMode(preset.id)"
                >
                  {{ preset.label }}
                </button>
              }
            </div>
          </div>

          <div class="website-builder__canvas-shell" #canvasViewportShell [ngStyle]="canvasShellStyle">
            <div class="website-builder__canvas-stage">
              <div class="website-builder__canvas-viewport-frame" [ngStyle]="canvasViewportFrameStyle">
            @if (showViewportRulers) {
              <div class="website-builder__viewport-ruler-corner"></div>
              <canvas
                class="website-builder__viewport-ruler-canvas website-builder__viewport-ruler-canvas--top"
                [ngStyle]="topRulerCanvasStyle"
                aria-hidden="true"
              ></canvas>
              <canvas
                class="website-builder__viewport-ruler-canvas website-builder__viewport-ruler-canvas--left"
                [ngStyle]="leftRulerCanvasStyle"
                aria-hidden="true"
              ></canvas>
            }

            @if (showViewportGrid) {
              <canvas class="website-builder__viewport-grid" [ngStyle]="gridStyle" aria-hidden="true"></canvas>
            }

            <div class="website-builder__canvas-viewport" [ngStyle]="scaledCanvasViewportStyle" (scroll)="handleCanvasViewportScroll($event)">
              <div
                class="website-builder__canvas-root"
                cdkDropList
                [class.is-selected]="builderService.selectedNodeId() === activeDocument.root.id"
                [ngStyle]="nodeStyles(activeDocument.root)"
                [id]="dropListId(activeDocument.root.id)"
                [cdkDropListData]="activeDocument.root.children"
                [cdkDropListConnectedTo]="connectedDropListIds"
                (cdkDropListDropped)="handleNodeDrop($event, activeDocument.root.id)"
                (click)="selectNode(activeDocument.root.id, $event)"
                (contextmenu)="openCanvasContextMenu($event, activeDocument.root)"
              >
                @if (!activeDocument.root.children.length) {
                  <div class="website-builder__empty-drop">
                    Select the page canvas and add a section or reusable component to start building the layout.
                  </div>
                }

                @for (child of activeDocument.root.children; track trackNode($index, child)) {
                  <ng-container [ngTemplateOutlet]="canvasNode" [ngTemplateOutletContext]="{ $implicit: child, parentId: activeDocument.root.id }"></ng-container>
                }

                @if (snapGuideX !== null) {
                  <div class="website-builder__snap-guide website-builder__snap-guide--vertical" [style.left.px]="snapGuideX"></div>
                }

                @if (snapGuideY !== null) {
                  <div class="website-builder__snap-guide website-builder__snap-guide--horizontal" [style.top.px]="snapGuideY"></div>
                }
              </div>
            </div>

            <button
              type="button"
              class="website-builder__canvas-resize-handle website-builder__canvas-resize-handle--corner-right"
              aria-label="Resize canvas"
              title="Resize canvas"
              (pointerdown)="startViewportResize($event, 'se')"
              (dblclick)="maximizeViewport()"
            ></button>
            <button
              type="button"
              class="website-builder__canvas-resize-handle website-builder__canvas-resize-handle--bottom"
              aria-label="Resize canvas height"
              title="Resize canvas height"
              (pointerdown)="startViewportResize($event, 's')"
              (dblclick)="maximizeViewportHeight()"
            ></button>
            <button
              type="button"
              class="website-builder__canvas-resize-handle website-builder__canvas-resize-handle--right"
              aria-label="Resize canvas width"
              title="Resize canvas width"
              (pointerdown)="startViewportResize($event, 'e')"
              (dblclick)="maximizeViewportWidth()"
            ></button>
            <button
              type="button"
              class="website-builder__canvas-resize-handle website-builder__canvas-resize-handle--left"
              aria-label="Resize canvas from left"
              title="Resize canvas from left"
              (pointerdown)="startViewportResize($event, 'w')"
              (dblclick)="maximizeViewportWidth()"
            ></button>
            <button
              type="button"
              class="website-builder__canvas-resize-handle website-builder__canvas-resize-handle--corner-left"
              aria-label="Resize canvas from bottom left"
              title="Resize canvas from bottom left"
              (pointerdown)="startViewportResize($event, 'sw')"
              (dblclick)="maximizeViewport()"
            ></button>
          </div>
        </div>
      </div>
      </div>
    </div>
    </section>

    @if (showSectionModal) {
      <div class="website-builder__modal-layer">
        <button type="button" class="website-builder__modal-scrim" aria-label="Close section presets" (click)="closeSectionModal()"></button>

        <section class="website-builder__modal">
          <div class="website-builder__modal-header">
            <div>
              <h3 class="website-builder__section-title mb-0 bw-title-with-info">
                Add section preset
                <app-info-tooltip compact placement="right" text="Section presets are technical starters for common page structures. They are intentionally reusable and editable rather than finished designs."></app-info-tooltip>
              </h3>
            </div>

            <button type="button" class="website-builder__icon-button" title="Close section presets" (click)="closeSectionModal()">
              <ion-icon name="close-outline"></ion-icon>
            </button>
          </div>

          <div class="website-builder__preset-grid mt-3">
            @for (preset of sectionPresets; track preset.id) {
              <button
                type="button"
                class="website-builder__preset-card"
                [style.--bw-preset-accent]="preset.accent"
                (click)="insertSectionPreset(preset.id)"
              >
                <div class="website-builder__preset-icon-wrap">
                  <ion-icon [name]="preset.icon"></ion-icon>
                </div>
                <div class="website-builder__preset-content">
                  <span class="website-builder__preset-label">{{ preset.label }}</span>
                  <span class="website-builder__preset-copy">{{ preset.description }}</span>
                </div>
              </button>
            }
          </div>
        </section>
      </div>
    }

    @if (showComponentModal) {
      <div class="website-builder__modal-layer">
        <button type="button" class="website-builder__modal-scrim" aria-label="Close component library" (click)="closeComponentModal()"></button>

        <section class="website-builder__modal website-builder__modal--wide">
          <div class="website-builder__modal-header">
            <div>
              <h3 class="website-builder__section-title mb-0 bw-title-with-info">
                Insert reusable component
                <app-info-tooltip compact placement="right" text="Components inserted here are cloned into the page so you can customize them locally. Save the edited selection as a new component if you want to reuse that variation later."></app-info-tooltip>
              </h3>
            </div>

            <button type="button" class="website-builder__icon-button" title="Close component library" (click)="closeComponentModal()">
              <ion-icon name="close-outline"></ion-icon>
            </button>
          </div>

          <div class="website-builder__component-filters mt-3">
            <label class="website-builder__field website-builder__field--search">
              <span class="website-builder__field-label">
                Search
                <app-info-tooltip compact placement="right" text="Search by component name, description, or tags to find a reusable block faster."></app-info-tooltip>
              </span>
              <input class="website-builder__input" type="text" [(ngModel)]="componentSearchTerm" placeholder="Search components" />
            </label>

            <label class="website-builder__field website-builder__field--source">
              <span class="website-builder__field-label">
                Source
                <app-info-tooltip compact placement="right" text="Built-in components are starter assets shipped with the platform. Custom components are your own reusable assets."></app-info-tooltip>
              </span>
              <select class="website-builder__select" [(ngModel)]="componentSourceFilter">
                <option value="all">All</option>
                <option value="built-in">Built-in</option>
                <option value="custom">Custom</option>
              </select>
            </label>
          </div>

          @if (componentLibraryLoading) {
            <div class="website-builder__component-loading py-4 d-flex justify-content-center">
              <ion-spinner name="crescent"></ion-spinner>
            </div>
          } @else if (componentLibraryError) {
            <app-api-load-error
              title="Component library unavailable"
              message="We couldn't load reusable components right now."
              retryLabel="Retry"
              (retry)="loadComponentLibrary(true)"
            ></app-api-load-error>
          } @else if (filteredComponentDefinitions.length) {
            <div class="website-builder__component-grid mt-3">
              @for (definition of filteredComponentDefinitions; track definition.id) {
                <button
                  type="button"
                  class="website-builder__component-card"
                  (click)="insertComponent(definition.id)"
                  (contextmenu)="openComponentCardContextMenu($event, definition.id)"
                >
                  <div class="website-builder__component-card-top">
                    <span class="website-builder__component-name">{{ definition.name }}</span>
                    <span class="website-builder__component-chip" [class.is-custom]="definition.source === 'custom'">{{ definition.source === 'built-in' ? 'Built-in' : 'Custom' }}</span>
                  </div>
                  <p class="website-builder__component-copy">{{ definition.description }}</p>
                  <div class="website-builder__component-tags">
                    @for (tag of definition.tags; track tag) {
                      <span class="website-builder__component-tag">{{ tag }}</span>
                    }
                  </div>
                </button>
              }
            </div>
          } @else {
            <div class="website-builder__component-empty mt-3">
              No reusable components match the current search or source filter.
            </div>
          }
        </section>
      </div>
    }

    @if (showThemeManagerModal) {
      <div class="website-builder__modal-layer">
        <button type="button" class="website-builder__modal-scrim" aria-label="Close canvas themes" (click)="closeThemeManagerModal()"></button>

        <section class="website-builder__modal website-builder__modal--wide">
          <div class="website-builder__modal-header">
            <div>
              <h3 class="website-builder__section-title mb-0 bw-title-with-info">Manage theme packages<app-info-tooltip compact placement="right" text="Create theme packages to override selected canvas colours without changing the default package."></app-info-tooltip></h3>
            </div>

            <div class="website-builder__modal-header-actions">
              <button type="button" class="website-builder__action-button" (click)="createThemePackage()">
                <ion-icon name="add-outline"></ion-icon>
                <span>New package</span>
              </button>
              <button type="button" class="website-builder__icon-button" title="Close themes" (click)="closeThemeManagerModal()">
                <ion-icon name="close-outline"></ion-icon>
              </button>
            </div>
          </div>

          <div class="website-builder__theme-manager mt-3">
            <aside class="website-builder__theme-package-list">
              @for (themePackage of canvasThemeService.packages(); track themePackage.id) {
                <button
                  type="button"
                  class="website-builder__theme-package-button"
                  [class.is-selected]="editingThemePackageId === themePackage.id"
                  (click)="chooseThemeDraft(themePackage.id)"
                >
                  <span class="website-builder__theme-package-name">{{ themePackage.name }}</span>
                  <span class="website-builder__theme-package-copy">{{ themePackage.description || 'Inherits defaults until overrides are added.' }}</span>
                </button>
              }
            </aside>

            <div class="website-builder__theme-editor">
              @if (themeDraft; as draft) {
                @if (draft.id === 'default') {
                  <section class="website-builder__theme-default-note">
                    <h4 class="website-builder__section-title mb-3 bw-title-with-info">Default preview colors<app-info-tooltip compact placement="right" text="The default package mirrors builder preview defaults. Create a new package and override only the values you need."></app-info-tooltip></h4>
                    <button type="button" class="website-builder__action-button is-primary" (click)="createThemePackage()">
                      <ion-icon name="add-outline"></ion-icon>
                      <span>Create override package</span>
                    </button>
                  </section>
                } @else {
                  <div class="website-builder__theme-editor-grid">
                    <label class="website-builder__field">
                      <span class="website-builder__field-label">Package name</span>
                      <input class="website-builder__input" type="text" [ngModel]="draft.name" (ngModelChange)="updateThemeDraftMeta('name', $event)" />
                    </label>

                    <label class="website-builder__field">
                      <span class="website-builder__field-label">Description</span>
                      <input class="website-builder__input" type="text" [ngModel]="draft.description" (ngModelChange)="updateThemeDraftMeta('description', $event)" />
                    </label>
                  </div>

                  <div class="website-builder__theme-columns mt-3">
                    <section class="website-builder__theme-column">
                      <div class="website-builder__theme-column-header">
                        <ion-icon name="sunny-outline"></ion-icon>
                        <strong>Light overrides</strong>
                      </div>

                      <div class="website-builder__theme-fields">
                        @for (field of canvasThemeFields; track field.key) {
                          <label class="website-builder__field">
                            <span class="website-builder__field-label">{{ field.label }}</span>
                            <input
                              class="website-builder__input"
                              type="text"
                              [ngModel]="draft.lightOverrides[field.key] || ''"
                              (ngModelChange)="updateThemeDraftOverride('light', field.key, $event)"
                            />
                          </label>
                        }
                      </div>
                    </section>

                    <section class="website-builder__theme-column">
                      <div class="website-builder__theme-column-header">
                        <ion-icon name="moon-outline"></ion-icon>
                        <strong>Dark overrides</strong>
                      </div>

                      <div class="website-builder__theme-fields">
                        @for (field of canvasThemeFields; track field.key) {
                          <label class="website-builder__field">
                            <span class="website-builder__field-label">{{ field.label }}</span>
                            <input
                              class="website-builder__input"
                              type="text"
                              [ngModel]="draft.darkOverrides[field.key] || ''"
                              (ngModelChange)="updateThemeDraftOverride('dark', field.key, $event)"
                            />
                          </label>
                        }
                      </div>
                    </section>
                  </div>

                  <div class="website-builder__theme-actions mt-3">
                    <button type="button" class="website-builder__action-button is-primary" (click)="saveThemePackage()">
                      <ion-icon name="save-outline"></ion-icon>
                      <span>Save package</span>
                    </button>
                    <button type="button" class="website-builder__action-button" (click)="selectCanvasThemePackage(draft.id)">
                      <ion-icon name="radio-button-on-outline"></ion-icon>
                      <span>Use package</span>
                    </button>
                    <button type="button" class="website-builder__action-button website-builder__action-button--danger" (click)="deleteThemePackage()">
                      <ion-icon name="close-outline"></ion-icon>
                      <span>Delete</span>
                    </button>
                  </div>
                }
              }
            </div>
          </div>
        </section>
      </div>
    }

    @if (showPreviewModal) {
      <div class="website-builder__modal-layer">
        <button type="button" class="website-builder__modal-scrim" aria-label="Close preview" (click)="closePreviewModal()"></button>

        <section class="website-builder__modal website-builder__modal--wide website-builder__modal--preview">
          <div class="website-builder__modal-header">
            <div>
              <h3 class="website-builder__section-title mb-0 bw-title-with-info">{{ currentPreviewPage?.name || activePage.name }} site preview<app-info-tooltip compact placement="right" text="Inspect the selected page using the current canvas size and theme before publishing."></app-info-tooltip></h3>
            </div>

            <div class="website-builder__modal-header-actions">
              <span class="website-builder__meta-chip">{{ currentCanvasThemeMode | titlecase }}</span>
              <span class="website-builder__meta-chip">{{ canvasWidth }} x {{ canvasHeight }}</span>
              <button type="button" class="website-builder__icon-button" title="Close preview" (click)="closePreviewModal()">
                <ion-icon name="close-outline"></ion-icon>
              </button>
            </div>
          </div>

          <div class="website-builder__preview-nav mt-3">
            <div class="website-builder__preview-nav-head">
              <div class="website-builder__toolbar-meta">
                <span class="website-builder__toolbar-label">Site pages</span>
                <app-info-tooltip compact placement="right" text="Use these page chips or click internal-page buttons inside the preview to move through the website without leaving the builder."></app-info-tooltip>
              </div>
              @if (currentPreviewPage) {
                <span class="website-builder__preview-path">{{ currentPreviewPage.path }}</span>
              }
            </div>

            <div class="website-builder__preview-page-list">
              @for (page of previewPages; track trackPage($index, page)) {
                <button
                  type="button"
                  class="website-builder__preview-page-button"
                  [class.is-active]="isPreviewPage(page)"
                  (click)="selectPreviewPage(page)"
                >
                  <span>{{ page.name }}</span>
                  @if (page.isHome) {
                    <span class="website-builder__preview-page-chip">Home</span>
                  }
                </button>
              }
            </div>
          </div>

          @if (previewError) {
            <div class="website-builder__preview-alert mt-3">{{ previewError }}</div>
          }

          <div class="website-builder__preview-frame mt-3" [ngStyle]="canvasShellStyle">
            <div class="website-builder__preview-stage">
              @if (isPreviewLoading) {
                <div class="website-builder__preview-loading">
                  <ion-spinner name="crescent"></ion-spinner>
                  <span>Loading linked page preview...</span>
                </div>
              } @else if (currentPreviewDocument) {
                <div class="website-builder__canvas-viewport is-preview" [ngStyle]="canvasViewportStyle">
                  <div class="website-builder__preview-root" [ngStyle]="nodeStyles(currentPreviewDocument.root)">
                    @for (child of currentPreviewDocument.root.children; track trackNode($index, child)) {
                      <ng-container [ngTemplateOutlet]="previewNode" [ngTemplateOutletContext]="{ $implicit: child }"></ng-container>
                    }
                  </div>
                </div>
              } @else {
                <div class="website-builder__preview-loading">
                  <span>No preview document is available.</span>
                </div>
              }
            </div>
          </div>
        </section>
      </div>
    }
  </div>

  <ng-template #canvasNode let-node let-parentId="parentId">
    <div
      class="website-builder__node"
      [class.is-selected]="builderService.selectedNodeId() === node.id"
      [class.is-hidden]="node.hidden"
      [class.is-absolute]="node.style.position === 'absolute'"
      [class.is-fluid-wrapper]="isFluidWrapperNode(node)"
      [ngClass]="previewNodeClasses(node)"
      [attr.data-node-id]="node.id"
      (click)="selectNode(node.id, $event)"
      (contextmenu)="openCanvasContextMenu($event, node)"
    >
      <div
        class="website-builder__node-surface bw-node"
        cdkDropList
        [cdkDropListDisabled]="!isContainer(node)"
        [id]="dropListId(node.id)"
        [cdkDropListData]="node.children"
        [cdkDropListConnectedTo]="connectedDropListIds"
        [ngStyle]="nodeStyles(node)"
        (cdkDropListDropped)="handleNodeDrop($event, node.id)"
        (pointerdown)="handleNodeSurfacePointerDown($event, node)"
      >
        @switch (node.type) {
          @case ('text') {
            @if (node.content.eyebrow) {
              <p class="bw-node-eyebrow">{{ node.content.eyebrow }}</p>
            }
            @if (node.content.title) {
              <h2 class="bw-node-title">{{ node.content.title }}</h2>
            }
            @if (node.content.text) {
              <p class="bw-node-copy">{{ node.content.text }}</p>
            }
            @if (node.content.secondaryText) {
              <p class="bw-node-copy bw-node-copy--secondary">{{ node.content.secondaryText }}</p>
            }
          }
          @case ('button') {
            <button type="button" class="bw-button-node">{{ node.content.buttonText || node.content.title || 'Button' }}</button>
          }
          @case ('media') {
            <div class="bw-media-node" [class.bw-media-node--with-image]="node.content.mediaUrl">
              @if (node.content.mediaUrl) {
                <img class="bw-media-image" [src]="resolveMediaUrl(node.content.mediaUrl)" [alt]="node.content.mediaAlt || ''" decoding="async" />
              }
              @if (node.content.mediaLabel || node.content.caption) {
                <div class="bw-media-content">
                  @if (node.content.mediaLabel) {
                    <span class="bw-media-label">{{ node.content.mediaLabel }}</span>
                  }
                  @if (node.content.caption) {
                    <span class="bw-media-caption">{{ node.content.caption }}</span>
                  }
                </div>
              }
            </div>
          }
          @case ('commerce') {
            <div class="website-builder__commerce-node">
              <div class="website-builder__commerce-toolbar">
                <span class="website-builder__commerce-kicker">Commerce runtime</span>
                <span class="website-builder__commerce-chip">{{ node.content.commerceWidget || 'storefront' }}</span>
              </div>
              <div class="website-builder__commerce-grid">
                <aside class="website-builder__commerce-filters">
                  <strong>Filtre catalog</strong>
                  <span>Categorii</span>
                  <span>Brand</span>
                  <span>Disponibilitate</span>
                </aside>
                <div class="website-builder__commerce-products">
                  @for (item of [1, 2, 3, 4, 5, 6]; track item) {
                    <article class="website-builder__commerce-product">
                      <div class="website-builder__commerce-product-image"></div>
                      <strong>Produs demo {{ item }}</strong>
                      <span>Descriere scurtă, rating, preț și stoc.</span>
                      <b>€{{ item * 100 + 99 }}</b>
                    </article>
                  }
                </div>
              </div>
            </div>
          }
          @case ('badge') {
            <span class="bw-badge-node">{{ node.content.badgeText || node.content.title || 'Badge' }}</span>
          }
        }

        @if (isContainer(node)) {
          @if (!node.children.length && builderService.selectedNodeId() === node.id) {
            <div class="website-builder__empty-drop is-inline">Select this container to add nested content.</div>
          }
          @for (child of node.children; track trackNode($index, child)) {
            <ng-container [ngTemplateOutlet]="canvasNode" [ngTemplateOutletContext]="{ $implicit: child, parentId: node.id }"></ng-container>
          }
        }

        @if (builderService.selectedNodeId() === node.id) {
          <div class="website-builder__node-overlay" (click)="$event.stopPropagation()">
          <button type="button" class="website-builder__resize-handle website-builder__resize-handle--n" (pointerdown)="startNodeResize($event, node, 'n')" aria-label="Resize from top"></button>
          <button type="button" class="website-builder__resize-handle website-builder__resize-handle--e" (pointerdown)="startNodeResize($event, node, 'e')" aria-label="Resize from right"></button>
          <button type="button" class="website-builder__resize-handle website-builder__resize-handle--s" (pointerdown)="startNodeResize($event, node, 's')" aria-label="Resize from bottom"></button>
          <button type="button" class="website-builder__resize-handle website-builder__resize-handle--w" (pointerdown)="startNodeResize($event, node, 'w')" aria-label="Resize from left"></button>
          <button type="button" class="website-builder__resize-handle website-builder__resize-handle--ne" (pointerdown)="startNodeResize($event, node, 'ne')" aria-label="Resize from top right"></button>
          <button type="button" class="website-builder__resize-handle website-builder__resize-handle--nw" (pointerdown)="startNodeResize($event, node, 'nw')" aria-label="Resize from top left"></button>
          <button type="button" class="website-builder__resize-handle website-builder__resize-handle--se" (pointerdown)="startNodeResize($event, node, 'se')" aria-label="Resize from bottom right"></button>
          <button type="button" class="website-builder__resize-handle website-builder__resize-handle--sw" (pointerdown)="startNodeResize($event, node, 'sw')" aria-label="Resize from bottom left"></button>
          </div>
        }
      </div>
    </div>
  </ng-template>

  <ng-template #layerNode let-node let-depth="depth">
    <div class="website-builder__tree-node">
      <div class="website-builder__tree-row" [class.is-selected]="builderService.selectedNodeId() === node.id" [style.paddingInlineStart]="layerPadding(depth)">
        @if (hasLayerChildren(node)) {
          <button type="button" class="website-builder__tree-toggle" [class.is-open]="isLayerExpanded(node.id)" (click)="toggleLayerExpansion(node.id, $event)" aria-label="Toggle child layers">
            <ion-icon name="chevron-forward-outline"></ion-icon>
          </button>
        } @else {
          <span class="website-builder__tree-toggle-spacer" aria-hidden="true"></span>
        }

        <button
          type="button"
          class="website-builder__layer-button website-builder__layer-button--tree"
          (click)="selectLayerNode(node.id, $event)"
          (contextmenu)="openLayerContextMenu($event, node)"
        >
          <ion-icon [name]="nodeIcon(node.type)"></ion-icon>
          <div class="website-builder__layer-meta">
            <span class="website-builder__layer-label">{{ node.label }}</span>
            <span class="website-builder__layer-type">{{ node.type | titlecase }}</span>
          </div>
        </button>
      </div>

      @if (hasLayerChildren(node) && isLayerExpanded(node.id)) {
        <div class="website-builder__tree-children">
          @for (child of node.children; track trackNode($index, child)) {
            <ng-container [ngTemplateOutlet]="layerNode" [ngTemplateOutletContext]="{ $implicit: child, depth: depth + 1 }"></ng-container>
          }
        </div>
      }
    </div>
  </ng-template>

  <ng-template #previewNode let-node>
    <div
      class="website-builder__preview-node"
      [ngClass]="previewNodeClasses(node)"
      [ngStyle]="nodeStyles(node)"
      (click)="handlePreviewNodeClick(node, $event)"
    >
      @switch (node.type) {
        @case ('text') {
          @if (node.content.eyebrow) {
            <p class="bw-node-eyebrow">{{ node.content.eyebrow }}</p>
          }
          @if (node.content.title) {
            <h2 class="bw-node-title">{{ node.content.title }}</h2>
          }
          @if (node.content.text) {
            <p class="bw-node-copy">{{ node.content.text }}</p>
          }
          @if (node.content.secondaryText) {
            <p class="bw-node-copy bw-node-copy--secondary">{{ node.content.secondaryText }}</p>
          }
        }
        @case ('button') {
          <button type="button" class="bw-button-node is-interactive" (click)="handlePreviewButtonClick(node)">
            {{ node.content.buttonText || node.content.title || 'Button' }}
          </button>
        }
        @case ('media') {
          <div class="bw-media-node" [class.bw-media-node--with-image]="node.content.mediaUrl">
            @if (node.content.mediaUrl) {
              <img class="bw-media-image" [src]="resolveMediaUrl(node.content.mediaUrl)" [alt]="node.content.mediaAlt || ''" decoding="async" />
            }
            @if (node.content.mediaLabel || node.content.caption) {
              <div class="bw-media-content">
                @if (node.content.mediaLabel) {
                  <span class="bw-media-label">{{ node.content.mediaLabel }}</span>
                }
                @if (node.content.caption) {
                  <span class="bw-media-caption">{{ node.content.caption }}</span>
                }
              </div>
            }
          </div>
        }
        @case ('commerce') {
          <div class="website-builder__commerce-node">
            <div class="website-builder__commerce-toolbar">
              <span class="website-builder__commerce-kicker">Commerce runtime</span>
              <span class="website-builder__commerce-chip">{{ node.content.commerceWidget || 'storefront' }}</span>
            </div>
            <div class="website-builder__commerce-grid">
              <aside class="website-builder__commerce-filters">
                <strong>Filtre catalog</strong>
                <span>Categorii</span>
                <span>Brand</span>
                <span>Disponibilitate</span>
              </aside>
              <div class="website-builder__commerce-products">
                @for (item of [1, 2, 3, 4, 5, 6]; track item) {
                  <article class="website-builder__commerce-product">
                    <div class="website-builder__commerce-product-image"></div>
                    <strong>Produs demo {{ item }}</strong>
                    <span>Descriere scurtă, rating, preț și stoc.</span>
                    <b>€{{ item * 100 + 99 }}</b>
                  </article>
                }
              </div>
            </div>
          </div>
        }
        @case ('badge') {
          <span class="bw-badge-node">{{ node.content.badgeText || node.content.title || 'Badge' }}</span>
        }
      }

      @if (isContainer(node) && node.children.length) {
        @for (child of node.children; track trackNode($index, child)) {
          <ng-container [ngTemplateOutlet]="previewNode" [ngTemplateOutletContext]="{ $implicit: child }"></ng-container>
        }
      }
    </div>
  </ng-template>
} @else {
  <div class="website-builder__empty-state">
    <h2 class="website-builder__section-title mb-0 bw-title-with-info">No page selected<app-info-tooltip compact placement="right" text="Open a website and page from the Websites area before using the page builder."></app-info-tooltip></h2>
  </div>
}
